import BigNumber from 'bignumber.js';
import { ITicker, IWalletTradeOverlay } from '@kattana-io/common';
import { WalletAPI, store } from '../../../../store/createStore';
import {
  GetMarksCallback,
  Mark,
  ResolutionString,
  TimescaleMark,
} from '../../charting_library/charting_library';
import { roundToPrecision } from '../../../../helpers/truncate';
import { capitalize } from '../../../Modal/TickersModal/helpers';
import { authTokenSelector } from '../../../../selectors/auth';

const getOverlayTradePriceAndAmount = (
  item: IWalletTradeOverlay,
  ticker: ITicker
) => {
  const amount0 = BigNumber(item.amount0)
    .div(10 ** ticker.base_decimals)
    .toNumber();
  const amount1 = BigNumber(item.amount1)
    .div(10 ** ticker.quote_decimals)
    .toNumber();
  const baseAmount = roundToPrecision(
    ticker.flipped ? amount1 : amount0,
    true,
    4
  );
  const quoteAmount = roundToPrecision(
    ticker.flipped ? amount0 : amount1,
    true,
    4
  );
  const price = roundToPrecision(
    ticker.flipped ? item.price_b : item.price_a,
    true,
    4
  );
  const priceUSD = roundToPrecision(
    ticker.flipped ? item.price_b_usd : item.price_a_usd,
    true,
    4
  );

  return { baseAmount, quoteAmount, price, priceUSD };
};

const fetchMarks = async (
  symbolInfo: any,
  from: number,
  to: number,
  onDataCallback: GetMarksCallback<Mark>,
  resolution: ResolutionString,
  usdConversion: boolean,
  address: string | null
) => {
  if (address) {
    const overlays = await WalletAPI.overlayWalletTrades(
      address,
      symbolInfo.chain,
      symbolInfo.ticker,
      from,
      to
    );
    if (overlays?.length) {
      const ticker = symbolInfo.data.ticker;
      const parsedData: Mark[] = overlays.map((item) => {
        const buy = ticker.flipped ? !item.buy : item.buy;
        const base = symbolInfo.data.base_asset;
        const quote = symbolInfo.data.quote_asset;

        const { baseAmount, quoteAmount, price, priceUSD } =
          getOverlayTradePriceAndAmount(item, ticker);

        const amountPart = `${
          buy ? 'Receive' : 'Send'
        } ${baseAmount} ${base} for ${quoteAmount} ${quote}`;
        const pricePart = `1 ${base} = ${
          usdConversion ? priceUSD + '$' : price + ' ' + quote
        }`;
        return {
          /** ID of the mark */
          id: `${item.tx}-${item.order}`,
          /**
           * Time for the mark.
           * Amount of **milliseconds** since Unix epoch start in **UTC** timezone.
           */
          time: item.timestamp,
          /** Color for the mark */
          color: buy ? 'green' : 'red',
          /** Text content for the mark */
          text: `${amountPart} (${pricePart})`,
          /** Label for the mark */
          label: (item.is_proxy_trade ? 'L' : '') + (buy ? 'B' : 'S'),
          /** Text color for the mark */
          labelFontColor: 'white',
          /** Minimum size for the mark */
          minSize: 25,
        };
      });
      onDataCallback(parsedData);
    }
  }
};

const fetchTimescaleMarks = async (
  symbolInfo: any,
  from: number,
  to: number,
  onDataCallback: GetMarksCallback<TimescaleMark>,
  resolution: ResolutionString
) => {
  const authToken = authTokenSelector(store.getState());
  const overlays = await WalletAPI.overlayDexEvents(
    symbolInfo.chain,
    symbolInfo.data.base_address,
    authToken
  );
  if (overlays?.length) {
    const parsedData: TimescaleMark[] = overlays.map((item) => {
      const parsedTooltip = item.tooltip.map((text) => {
        const [first, second] = text.split(' ');
        const parsedSecond = Number.isNaN(Number(second))
          ? second
          : roundToPrecision(Number(second), true, 2);
        return capitalize(first.replaceAll('_', ' ')) + ' ' + parsedSecond;
      });
      return {
        /** ID of the timescale mark */
        id: item.id,
        /**
         * Time for the mark.
         * Amount of **milliseconds** since Unix epoch start in **UTC** timezone.
         */
        time: item.date / 1000,
        /** Color for the timescale mark */
        color: 'green',
        /**
         * Color for the timescale mark text label.
         * If undefined then the value provided for `color` will be used.
         */
        // labelFontColor: 'green',
        /** Label for the timescale mark */
        label: item.label === 'unlock' ? 'U' : item.label,
        /** Tooltip content */
        tooltip: parsedTooltip,
        /** Shape of the timescale mark */
        shape: 'circle',
      };
    });
    onDataCallback(parsedData);
  }
};

export { fetchMarks, fetchTimescaleMarks };
